import { useMemo } from 'react';
import { Cell, Pie, PieChart } from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
type Props = {
  loading: boolean;
  data: { label: string; value: number }[];
};

const outcomeChartConfig = {
  Answered: {
    label: 'Answered',
    theme: {
      light: 'oklch(0.515 0.145 155)',
      dark: 'oklch(0.6 0.18 155)',
    },
  },
  Initiated: {
    label: 'Initiated',
    theme: {
      light: 'oklch(0.45 0.14 230)',
      dark: 'oklch(0.6 0.16 230)',
    },
  },
  Ongoing: {
    label: 'Ongoing',
    theme: {
      light: 'oklch(0.68 0.17 85)',
      dark: 'oklch(0.75 0.18 85)',
    },
  },
  Busy: {
    label: 'Busy',
    theme: {
      light: 'oklch(0.55 0.18 300)',
      dark: 'oklch(0.65 0.2 300)',
    },
  },
  Voicemail: {
    label: 'Voicemail',
    theme: {
      light: 'oklch(0.48 0.2 280)',
      dark: 'oklch(0.6 0.22 280)',
    },
  },
  Unanswered: {
    label: 'Unanswered',
    theme: {
      light: 'oklch(0.55 0.22 25)',
      dark: 'oklch(0.62 0.24 25)',
    },
  },
  Error: {
    label: 'Error',
    theme: {
      light: 'oklch(0.58 0.22 315)',
      dark: 'oklch(0.65 0.24 315)',
    },
  },
} satisfies ChartConfig;

export const CallBreakdownChart = ({ loading, data }: Props) => {
  const outcomeData = useMemo(() => {
    if (data && data.length > 0) {
      return data.map(item => ({
        name: item.label,
        value: item.value,
      }));
    }

    return [
      { name: 'Initiated', value: 0 },
      { name: 'Answered', value: 0 },
      { name: 'Ongoing', value: 0 },
      { name: 'Busy', value: 0 },
      { name: 'Voicemail', value: 0 },
      { name: 'Unanswered', value: 0 },
      { name: 'Error', value: 0 },
    ];
  }, [data]);

  return (
    <Card className="bg-muted rounded-2xl p-1 pt-0 gap-0 border-0 shadow-none flex flex-col">
      <CardHeader className="flex flex-col px-5 py-3">
        <CardTitle className="text-sm">Call outcomes breakdown</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 bg-card p-5 rounded-xl shadow-sm flex-1">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className=" w-full min-h-[200px]" />
          </div>
        ) : (
          <ChartContainer config={outcomeChartConfig} className="h-[240px]">
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent />} />
              <Pie
                data={outcomeData}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={90}
              >
                {outcomeData.map(entry => (
                  <Cell key={entry.name} fill={`var(--color-${entry.name})`} />
                ))}
              </Pie>
              <ChartLegend
                content={
                  <ChartLegendContent nameKey="name" className="flex-col items-start gap-2" />
                }
                verticalAlign="middle"
                align="right"
                layout="vertical"
                wrapperStyle={{
                  paddingLeft: '20px',
                }}
              />
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
